import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

// Timeline Modal Component
const GymTimelineModal = ({ isOpen, onClose, gymId, gymName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timelineData, setTimelineData] = useState(null);

  useEffect(() => {
    if (isOpen && gymId) {
      fetchGymTimeline();
    }
  }, [isOpen, gymId]);

  const fetchGymTimeline = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/gyms/${gymId}/timeline`);
      setTimelineData(response.data);
    } catch (error) {
      console.error('Error fetching gym timeline:', error);
      setError(error.response?.data?.error || 'Failed to fetch timeline');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTimelineData(null);
    onClose();
  };

  if (!isOpen) return null;

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': { bg: 'bg-gray-100', border: 'border-gray-400', text: 'text-gray-800', dot: 'bg-gray-400' },
      'Site Visit': { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-800', dot: 'bg-blue-500' },
      'Proposal Sent': { bg: 'bg-yellow-100', border: 'border-yellow-400', text: 'text-yellow-800', dot: 'bg-yellow-500' },
      'Agreement Signed': { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-800', dot: 'bg-green-500' },
      'Facility Work': { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-800', dot: 'bg-purple-500' },
      'Post Reki Approved': { bg: 'bg-indigo-100', border: 'border-indigo-400', text: 'text-indigo-800', dot: 'bg-indigo-500' },
      'Gym Onboard': { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-800', dot: 'bg-red-500' }
    };
    return colors[stage] || colors['New Lead'];
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'gym_created':
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
      case 'stage_change':
        return (
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        );
      case 'task_approved':
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'task_completed':
        return (
          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'visit_logged':
        return (
          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getEventBgColor = (type) => {
    switch (type) {
      case 'gym_created':
        return 'bg-green-50 border-green-200';
      case 'stage_change':
        return 'bg-blue-50 border-blue-200';
      case 'task_approved':
        return 'bg-green-50 border-green-200';
      case 'task_completed':
        return 'bg-purple-50 border-purple-200';
      case 'visit_logged':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const renderTimeline = () => {
    if (!timelineData?.chronologicalTimeline || timelineData.chronologicalTimeline.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No timeline events available</p>
        </div>
      );
    }

    return (
      <div className="relative">
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
        <div className="space-y-4">
          {timelineData.chronologicalTimeline.map((event, index) => (
            <div key={index} className="relative flex items-start">
              <div className="flex-shrink-0 w-12 flex justify-center">
                <div className={`w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center shadow-sm`}>
                  {getEventTypeIcon(event.type)}
                </div>
              </div>
              <div className="ml-4 flex-1">
                <div className={`${getEventBgColor(event.type)} border rounded-lg p-3`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800 text-sm">{event.title}</h4>
                      {event.description && (
                        <p className="text-xs text-gray-600 mt-1">{event.description}</p>
                      )}
                      {event.user && (
                        <p className="text-xs text-gray-500 mt-1">
                          By: {event.user}
                          {event.role && <span className="text-gray-400"> ({event.role})</span>}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-xs text-gray-600">
                        {format(new Date(event.timestamp), 'MMM dd, yyyy')}
                      </div>
                      <div className="text-xs text-gray-400">
                        {format(new Date(event.timestamp), 'hh:mm a')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (!timelineData?.gym) return null;

    const gym = timelineData.gym;
    const stageColor = getStageColor(gym.stage);

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-blue-600">{timelineData.stats?.stageChanges || 0}</div>
          <div className="text-xs text-blue-700">Stage Changes</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-green-600">{timelineData.stats?.totalApprovals || 0}</div>
          <div className="text-xs text-green-700">Approvals</div>
        </div>
        <div className={`${stageColor.bg} rounded-lg p-3 text-center`}>
          <div className={`text-sm font-bold ${stageColor.text}`}>{gym.stage}</div>
          <div className={`text-xs ${stageColor.text} opacity-80`}>Current Stage</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-orange-600">
            {gym.location?.split(',')[0] || 'N/A'}
          </div>
          <div className="text-xs text-orange-700">Location</div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {gymName || 'Gym'} Timeline
            </h2>
            <p className="text-sm text-blue-100">Acquisition journey</p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-2">{error}</div>
              <button
                onClick={fetchGymTimeline}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {renderSummary()}
              {renderTimeline()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

function safeFormat(date, fmt) {
  if (!date) return 'Not set';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d)) return 'Not set';
    return format(d, fmt);
  } catch {
    return 'Not set';
  }
}

const BDExecutiveDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalGyms: 0,
    gymsByStage: [],
    recentGyms: [],
    upcomingLaunches: []
  });
  const [timelineModal, setTimelineModal] = useState({
    isOpen: false,
    gymId: null,
    gymName: ''
  });

  // Metric modal state
  const [metricModal, setMetricModal] = useState({
    isOpen: false,
    title: '',
    stage: null,
    gyms: [],
    loading: false
  });

  const openTimelineModal = (gym) => {
    setTimelineModal({
      isOpen: true,
      gymId: gym.id,
      gymName: gym.name
    });
  };

  const closeTimelineModal = () => {
    setTimelineModal({
      isOpen: false,
      gymId: null,
      gymName: ''
    });
  };

  // Open metric modal with gyms filtered by stage
  const openMetricModal = async (stage, title) => {
    setMetricModal({ isOpen: true, title, stage, gyms: [], loading: true });

    try {
      const response = await axios.get('/api/gyms?limit=100');
      const allGyms = response.data.gyms || [];
      const filteredGyms = allGyms.filter(gym => gym.stage === stage);
      setMetricModal(prev => ({ ...prev, gyms: filteredGyms, loading: false }));
    } catch (error) {
      console.error('Error fetching gyms:', error);
      toast.error('Failed to load gyms');
      setMetricModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeMetricModal = () => {
    setMetricModal({ isOpen: false, title: '', stage: null, gyms: [], loading: false });
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/gyms/stats/overview');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary statistics
  const getStageCount = (stage) => {
    const stageData = dashboardData.gymsByStage.find(s => s.stage === stage);
    return stageData ? stageData._count : 0;
  };

  const leadsCreated = getStageCount('New Lead');
  const siteVisitsDone = getStageCount('Site Visit');
  const proposalsSent = getStageCount('Proposal Sent');
  const agreementsSigned = getStageCount('Agreement Signed');

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': 'bg-gray-100 text-gray-800',
      'Site Visit': 'bg-blue-100 text-blue-800',
      'Proposal Sent': 'bg-yellow-100 text-yellow-800',
      'Agreement Signed': 'bg-green-100 text-green-800',
      'Facility Work': 'bg-purple-100 text-purple-800',
      'Post Reki Approved': 'bg-indigo-100 text-indigo-800',
      'Gym Onboard': 'bg-red-100 text-red-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg text-white p-6">
        <h1 className="text-2xl font-bold">Welcome, {user?.name}! 👋</h1>
        <p className="mt-2 text-blue-100">Track your gym acquisition progress and manage your pipeline.</p>
      </div>

      {/* Summary Cards - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Leads Created */}
        <div
          onClick={() => openMetricModal('New Lead', 'Leads Created')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Leads Created</p>
              <p className="text-2xl font-bold text-gray-900">{leadsCreated}</p>
            </div>
          </div>
        </div>

        {/* Site Visits Done */}
        <div
          onClick={() => openMetricModal('Site Visit', 'Site Visits Done')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Site Visits Done</p>
              <p className="text-2xl font-bold text-gray-900">{siteVisitsDone}</p>
            </div>
          </div>
        </div>

        {/* Proposals Sent */}
        <div
          onClick={() => openMetricModal('Proposal Sent', 'Proposals Sent')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-yellow-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Proposals Sent</p>
              <p className="text-2xl font-bold text-gray-900">{proposalsSent}</p>
            </div>
          </div>
        </div>

        {/* Agreements Signed */}
        <div
          onClick={() => openMetricModal('Agreement Signed', 'Agreements Signed')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-green-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Agreements Signed</p>
              <p className="text-2xl font-bold text-gray-900">{agreementsSigned}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/gyms/create" className="flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors group">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-900">Add New Gym</p>
              <p className="text-xs text-blue-600">Create a new gym lead</p>
            </div>
          </Link>
          <Link to="/kanban" className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors group">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-900">Kanban Board</p>
              <p className="text-xs text-green-600">Track gym pipeline</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Gyms Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">My Gyms</h3>
          <p className="text-sm text-gray-600">Gyms created or assigned to you</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gym Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Stage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target Launch Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dashboardData.recentGyms.length > 0 ? (
                dashboardData.recentGyms.map((gym) => (
                  <tr key={gym.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{gym.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{gym.location}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(gym.stage)}`}>
                        {gym.stage}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {safeFormat(gym.launch_date, 'MMM dd, yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openTimelineModal(gym)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        View Details
                      </button>
                      <Link
                        to="/kanban"
                        className="text-green-600 hover:text-green-900"
                      >
                        Move Stage
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="text-gray-400 text-6xl mb-4">🏋️</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No gyms yet</h3>
                    <p className="text-gray-600 mb-4">Start by creating your first gym lead</p>
                    <Link to="/gyms/create" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                      Add New Gym
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {dashboardData.recentGyms.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Link to="/gyms" className="text-blue-600 hover:text-blue-900 text-sm font-medium">
              View all gyms →
            </Link>
          </div>
        )}
      </div>

      {/* Upcoming Launches */}
      {dashboardData.upcomingLaunches.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Upcoming Launches</h3>
            <p className="text-sm text-gray-600">Gyms launching in the next 30 days</p>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {dashboardData.upcomingLaunches.map((gym) => (
                <div key={gym.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors" onClick={() => openTimelineModal(gym)}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{gym.name}</p>
                    <p className="text-xs text-gray-600">{gym.location}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {safeFormat(gym.launch_date, 'MMM dd, yyyy')}
                    </p>
                    <p className="text-xs text-gray-600">Target Launch</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Timeline Modal */}
      <GymTimelineModal
        isOpen={timelineModal.isOpen}
        onClose={closeTimelineModal}
        gymId={timelineModal.gymId}
        gymName={timelineModal.gymName}
      />

      {/* Metric Detail Modal */}
      {metricModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{metricModal.title}</h2>
                <p className="text-sm text-gray-500 mt-1">Gyms in {metricModal.stage} stage</p>
              </div>
              <button
                onClick={closeMetricModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {metricModal.loading ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : metricModal.gyms.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-5xl mb-4">🏋️</div>
                  <p className="text-gray-500">No gyms in this stage</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gym Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target Launch</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {metricModal.gyms.map((gym) => (
                        <tr key={gym.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {gym.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {gym.location || gym.city || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {safeFormat(gym.launch_date, 'MMM dd, yyyy')}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <button
                              onClick={() => {
                                closeMetricModal();
                                openTimelineModal(gym);
                              }}
                              className="text-blue-600 hover:text-blue-800 font-medium mr-3"
                            >
                              Timeline
                            </button>
                            <Link
                              to={`/gyms/${gym.id}`}
                              className="text-green-600 hover:text-green-800 font-medium"
                              onClick={closeMetricModal}
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-xl">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {metricModal.gyms.length} {metricModal.gyms.length === 1 ? 'gym' : 'gyms'}
                </span>
                <button
                  onClick={closeMetricModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BDExecutiveDashboard; 